import ActionButton from "Components/atoms/buttons/action-button";
import { useRef, useState, useCallback, useLayoutEffect, useMemo, ChangeEventHandler, ChangeEvent } from "react";
import { OrgChart } from 'd3-org-chart';
import jspdf from 'jspdf';
import RamLogo from '../../../assets/images/ram-logo.png'
import * as d3 from 'd3';
import { D3OrgChartNode, Department } from "models/org-chart-node";
import SelectInput from "Components/molecule/inputs/select-input";
import { SyncIcon, HelpIcon, ControlsIcon, CloseCircleIcon, FitIcon, SwapIcon, CompactIcon, FullscreenIcon, ImageIcon, SvgIcon, PdfIcon, ExpandAllIcon, CollapseAllIcon, ZoomOutIcon, ZoomInIcon } from "Components/atoms/icons";
import logoLightPng from "../../../assets/images/logo-horizontal.png";

interface OrgChartComponentProps {
    data?: D3OrgChartNode[]
    charts?: Department[]
    focusedChart: Department | null;
    childrenMap: Map<string, boolean>,
    setFocusedChart?: (_chart: Department) => void
    shiftHeight?: number
    handleSyncData?: () => any
    fullScreen: boolean
    handleOpenHelpDocument?: () => any,
}

const OrganizationChart = ({
    data,
    charts,
    setFocusedChart,
    focusedChart,
    childrenMap,
    shiftHeight = 0,
    handleSyncData,
    fullScreen = false,
    handleOpenHelpDocument
}: OrgChartComponentProps) => {
    const d3Container = useRef(null);
    const chartRef = useRef<any>(null);
    const [index, setIndex] = useState(0);
    const [compact, setCompact] = useState(0);
    const [actNdCent, setActNdCent] = useState(0);
    const [showControls, setShowControls] = useState<boolean>(true);
    // Set when the chart library cannot draw the data (e.g. two people reporting to each other):
    // a message instead of a blank page.
    const [renderError, setRenderError] = useState<string | null>(null);
    const [showWarnings, setShowWarnings] = useState<boolean>(true);

    // Employees whose manager is terminated / inactive (moved up by transformNodes)
    const inactiveManagerNodes = useMemo(() => (data ?? []).filter(n => n.inactiveManager), [data]);

    const getViewPortDemensions = useCallback(() => ({
        width: window.innerWidth,
        height: window.innerHeight
    }), [])

    const updateChartDemensions = useCallback(() => {
        if (chartRef.current) {
            const { width, height } = getViewPortDemensions();
            chartRef.current.svgWidth(width).svgHeight(height).render()
        }
    }, [getViewPortDemensions])


    useLayoutEffect(() => {
        setRenderError(null)
        try {
        if (data && data.length > 0 && d3Container.current) {
            const { width, height } = getViewPortDemensions();
            chartRef.current = new OrgChart()
                .container(d3Container.current)
                .data(data)
                .svgWidth(width)
                .svgHeight(height - shiftHeight)
                .nodeWidth((d) => 400)
                .nodeHeight((d) => 80)
                .siblingsMargin(() => 50)
                .childrenMargin(() => 100)
                .neighbourMargin((node1, node2) => 40)
                .layout('left')
                .onNodeClick((d: any) => {
                    const mockEvent = {
                        stopPropagation: () => { },
                        preventDefault: () => { }
                    };

                    if (d.children || d._children) {
                        chartRef.current.onButtonClick(mockEvent, d);
                    }
                })
                .buttonContent(() => '')
                .compact(false)
                .compactMarginBetween((d) => 50)
                .nodeButtonWidth(() => 0)
                .nodeButtonHeight(() => 0)
                .rootMargin(40)
                .linkUpdate((d: any, i: number, arr: any[]) => {
                    d3.select(arr[i] as any)
                        .style("stroke", "var(--bs-primary)")
                        .attr("stroke-width", 2);
                })
                .nodeContent((d: any, i: number, arr: any[], state: any) => {
                    const node = (d.data) as D3OrgChartNode;
                    return `
                        <div class="org-chart-node ${node.employeeType}" style="
                            width: ${d.width}px; 
                            height: ${d.height}px; 
                            border-radius: 4px; 
                            border: 1px solid var(--bs-border-color); 
                            ${node.inactiveManager ? 'outline: 3px solid var(--bs-danger); outline-offset: -3px;' : ''}
                            border-left: 15px solid ${node.employeeType === 'Employee' || node.employeeType === 'Coop' ? 'var(--bs-primary)' : node.employeeType === 'Consultant' ? '#F26722' : 'var(--bs-border-color)'}; 
                            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); 
                            padding: 8px 12px; 
                            background: ${node.employeeType === 'Employee' || node.employeeType === 'Coop' ? 'var(--bs-secondary-bg)' : node.employeeType === 'Consultant' ? 'color-mix(in srgb, var(--bs-orange) 35%, var(--bs-body-bg))' : 'var(--bs-body-bg)'}; 
                            color: var(--bs-body-color); 
                            font-family: 'Arial', sans-serif; 
                            position: relative; 
                            overflow: hidden; 
                            cursor: pointer; 
                            transition: all 0.3s ease;
                        ">
                            <div class="org-chart-node-name" style="
                                font-size: 24px; 
                                font-weight: bold; 
                                text-align: left; 
                                margin-bottom: 2px; 
                                line-height: 1.2; 
                                color: var(--bs-emphasis-color);
                            ">
                                ${node.name || 'Unknown'}
                            </div>
                            ${childrenMap.has(node.id) ? `<div style="
                                position: absolute;
                                bakcground-color
                                overflow: visible; 
                                right: -2.5px; 
                                border-radius: 50%;
                                top: 45%; 
                                height: 10px;
                                width: 10px; 
                                background-color: #F26722
                                "></div>` : ''}
                            <div class="org-chart-title" style="
                                font-size: 16px; 
                                text-align: left; 
                                opacity: 0.9; 
                                margin-bottom: 0; 
                                font-weight: normal; 
                                line-height: 1.2; 
                                color: var(--bs-emphasis-color);
                            ">
                                ${node.title || 'No title'}
                            </div>
                            ${node.inactiveManager ? `<div style="
                                font-size: 13px;
                                font-weight: 600;
                                text-align: left;
                                line-height: 1.2;
                                margin-top: 2px;
                                color: var(--bs-danger);
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                            ">&#9888; Manager inactive: ${node.inactiveManager}</div>` : ''}
                        </div>
                    `;
                })
                .render();
        }

        chartRef.current && chartRef.current.fit();
        } catch (err: any) {
            console.error("Org chart could not be drawn:", err);
            chartRef.current = null;
            setRenderError(err?.message ?? String(err));
        }

        const handleResize = () => {
            updateChartDemensions();
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, [data]);

    // Action handlers
    const handleExpand = () => {
        if (chartRef.current && data && data.length > 0) {
            chartRef.current.setExpanded(data[0].id).render();
        }
    };

    const handleCollapse = () => {
        if (chartRef.current && data && data.length > 0) {
            chartRef.current.setExpanded(data[0].id, false).render();
        }
    };

    const handleFit = () => {
        if (chartRef.current) {
            chartRef.current.fit();
        }
    };

    const handleSwap = () => {
        if (chartRef.current) {
            const layouts = ["right", "bottom", "left", "top"];
            const newIndex = (index + 1) % 4;
            setIndex(newIndex);
            chartRef.current.layout(layouts[newIndex]).render().fit();
        }
    };

    const handleCompact = () => {
        if (chartRef.current) {
            const newCompact = (compact + 1) % 2;
            setCompact(newCompact);
            chartRef.current.compact(!!newCompact).render().fit();
        }
    };

    const handleCenterActive = () => {
        if (chartRef.current) {
            const newActNdCent = (actNdCent + 1) % 2;
            setActNdCent(newActNdCent);
            chartRef.current.setActiveNodeCentered(!!newActNdCent).render();
        }
    };

    const handleCenter = () => {
        if (chartRef.current && data && data.length > 0) {
            chartRef.current.setCentered(data[0].id).render();
        }
    };

    const handleMark = () => {
        if (chartRef.current && data && data.length > 0) {
            chartRef.current.setHighlighted(data[0].id).render();
        }
    };

    const handleMarkRoot = () => {
        if (chartRef.current && data && data.length > 0) {
            chartRef.current.setUpToTheRootHighlighted(data[0].id).render().fit();
        }
    };

    const handleClearMark = () => {
        if (chartRef.current) {
            chartRef.current.clearHighlighting();
        }
    };

    const handleFullscreen = () => {
        if (chartRef.current) {
            chartRef.current.fullscreen('body');
        }
    };

    const handleExportImg = () => {
        if (chartRef.current) {
            chartRef.current.exportImg();
        }
    };

    const handleExportFullImg = () => {
        if (chartRef.current) {
            chartRef.current.exportImg({ full: true });
        }
    };

    const handleExportSvg = () => {
        if (chartRef.current) {
            chartRef.current.exportSvg();
        }
    };

    const handleExpandAll = () => {
        if (chartRef.current) {
            chartRef.current.expandAll();
        }
    };

    const handleCollapseAll = () => {
        if (chartRef.current) {
            chartRef.current.collapseAll();
        }
    };

    const handleZoomOut = () => {
        if (chartRef.current) {
            chartRef.current.zoomOut();
        }
    };

    const handleZoomIn = () => {
        if (chartRef.current) {
            chartRef.current.zoomIn();
        }
    };

    const handleExportPdf = () => {
        if (!chartRef.current) {
            return
        }
        chartRef.current.exportImg({
            save: false,
            onLoad: (base64: string) => {
                var pdf = new jspdf();
                var img = new Image();
                img.src = base64;
                img.onload = function () {
                    pdf.addImage(
                        img,
                        'JPEG',
                        5,
                        5,
                        595 / 3,
                        ((img.height / img.width) * 595) / 3
                    );
                    pdf.save('chart.pdf');
                };
            },
        });
    }

    // Fixed: Changed 'close' to 'closed'
    const show = showControls ? 'open' : 'closed';

    return (<div className="organization-chart-template">
        {/* Chart Container - now takes full space */}
        <div ref={d3Container} style={renderError ? { display: 'none' } : undefined} />

        {renderError &&
            <div className="org-chart-alert alert alert-danger">
                <strong>The organization chart could not be drawn.</strong> The employee / manager data has a problem
                (for example two people reporting to each other). Fix the managers in Dayforce and sync again.
                <div className="small text-muted mt-1">{renderError}</div>
            </div>}

        {!renderError && showWarnings && inactiveManagerNodes.length > 0 &&
            <div className="org-chart-alert alert alert-warning alert-dismissible">
                <strong>
                    {inactiveManagerNodes.length} employee{inactiveManagerNodes.length === 1 ? '' : 's'} report{inactiveManagerNodes.length === 1 ? 's' : ''} to
                    an inactive manager
                </strong> (shown under the next manager up, highlighted in red). Update the manager in Dayforce and sync.
                <ul className="mb-0 mt-1 ps-3">
                    {inactiveManagerNodes.map(n => <li key={n.id}>{n.name} &rarr; {n.inactiveManager}</li>)}
                </ul>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowWarnings(false)} />
            </div>}

        {
            charts && setFocusedChart &&
            <div className={`select-chart-container ${show}`}>
                <SelectInput
                    label={""}
                    value={focusedChart?.id}
                    options={charts.map((d: Department) => ({ label: d.description, value: d.id }))}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        const val = e.target.value;
                        const chart = charts.find((d: Department) => d.id === val)
                        chart && setFocusedChart(chart)
                    }}
                >

                </SelectInput>
            </div>
        }

        <div
            className={`arrow-button-container ${show}`}
            onClick={() => setShowControls(true)}
            style={fullScreen ? { position: 'fixed' } : undefined}
        >
            <span><ControlsIcon /></span>
        </div>

        {/* Action Panel */}
        <div
            className={`action-button-container ${show}`}
            style={fullScreen ? { position: 'fixed' } : undefined}

        >
            <ActionButton onClick={() => setShowControls(false)}>
                <span><CloseCircleIcon /></span> close
            </ActionButton>

            <ActionButton onClick={handleFit}>
                <span><FitIcon /> fit</span>
            </ActionButton>

            <ActionButton onClick={handleSwap}>
                <span><SwapIcon /> swap</span>
            </ActionButton>

            <ActionButton onClick={handleCompact}>
                <span><CompactIcon /> compact</span>
            </ActionButton>

            <ActionButton onClick={handleFullscreen}>
                <span><FullscreenIcon /> fullscreen</span>
            </ActionButton>

            <ActionButton onClick={handleExportImg}>
                <span><ImageIcon /> export img</span>
            </ActionButton>

            <ActionButton onClick={handleExportSvg}>
                <span><SvgIcon /> export svg</span>
            </ActionButton>

            <ActionButton onClick={handleExportPdf}>
                <span><PdfIcon /> export pdf</span>
            </ActionButton>

            <ActionButton onClick={handleExpandAll}>
                <span><ExpandAllIcon /> expand all</span>
            </ActionButton>

            <ActionButton onClick={handleCollapseAll}>
                <span><CollapseAllIcon /> collapse all</span>
            </ActionButton>

            <ActionButton onClick={handleZoomOut}>
                <span><ZoomOutIcon /> zoom out</span>
            </ActionButton>

            <ActionButton onClick={handleZoomIn}>
                <span><ZoomInIcon /> zoom in</span>
            </ActionButton>
            {
                handleSyncData &&
                <ActionButton
                    onClick={handleSyncData}
                >
                    <span><SyncIcon /> Sync Data</span>
                </ActionButton>
            }


            {
                handleOpenHelpDocument &&
                <ActionButton
                    onClick={() => handleOpenHelpDocument()}
                >
                    <span><HelpIcon /> Help</span>
                </ActionButton>
            }

        </div>

        {
            fullScreen &&
            <div className="logo-container open"
                style={{ position: 'fixed' }}
            >
                <img src={logoLightPng} alt="" height="50" />
            </div>
        }
    </div>
    );
}

export default OrganizationChart;


